/**
 * SearchableSelect
 * A combobox-style select with search for large option lists. Inside a
 * FormField the trigger is named "label + what it shows" (aria-labelledby),
 * so a screen reader hears "Client, Francy & Frederico" after a pick; give
 * it the field's htmlFor as `id` so the label points at it too. Alone, the
 * placeholder names it.
 */

import type { FC, ReactNode } from 'react';
import { useId, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { useFormFieldLabelId } from '../fields/formFieldLabel';
import { searchableSelectKeys } from './searchableSelectKeys';
import { SearchableSelectList } from './SearchableSelectList';
import { SearchableSelectSearch } from './SearchableSelectSearch';
import { useSearchableSelect } from './useSearchableSelect';

export interface SearchableSelectOption<T = string | number> {
  value: T;
  /** Used for trigger display and search/filter. */
  label: string;
  /** Optional rich content for dropdown list (e.g. badges). Falls back to label when absent. */
  optionContent?: ReactNode;
}

export interface SearchableSelectProps<T = string | number> {
  options: SearchableSelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** The trigger's id — the htmlFor of the FormField around it, so its label points here. */
  id?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  className?: string;
  /** Renders inside the popover header (border-b p-2), e.g. tag filter. */
  headerSlot?: ReactNode;
}

const TRIGGER = cn(
  'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-background px-3 text-left text-sm',
  'transition-colors placeholder:text-muted-foreground',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  'disabled:cursor-not-allowed disabled:opacity-50',
);
const CONTENT = cn(
  'z-[250] w-[max(320px,var(--radix-popover-trigger-width))] min-w-[320px] max-w-[360px] rounded-lg border border-border bg-popover p-0 text-popover-foreground shadow-md',
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
);

/** In a field: named by its label + the shown value. Alone: by the placeholder. */
const triggerNaming = (labelId: string | undefined, valueId: string, placeholder: string | undefined) =>
  labelId ? { 'aria-labelledby': `${labelId} ${valueId}` } : { 'aria-label': placeholder };

export function SearchableSelect<T extends string | number>({
  options,
  value,
  onChange,
  id,
  placeholder,
  searchPlaceholder,
  disabled = false,
  className,
  headerSlot,
}: SearchableSelectProps<T>): ReturnType<FC<SearchableSelectProps<T>>> {
  const { t } = useTranslation('common');
  const listRef = useRef<HTMLDivElement>(null);
  const labelId = useFormFieldLabelId();
  const valueId = useId();
  const { open, setOpen, search, setSearch, filtered, pick, openChange } = useSearchableSelect(options, onChange);
  const selectedOption = options.find((o) => o.value === value);
  const onKeyDown = searchableSelectKeys({ open, options: filtered, value, listRef, onChange, onPick: pick, onClose: () => setOpen(false) });
  return (
    <Popover.Root open={open} onOpenChange={openChange}>
      <Popover.Trigger asChild>
        <button type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} id={id} {...triggerNaming(labelId, valueId, placeholder)} className={cn(TRIGGER, !selectedOption && 'text-muted-foreground', className)}>
          <span id={valueId} className="truncate">
            {selectedOption?.label ?? placeholder ?? t('status.select')}
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className={CONTENT} onCloseAutoFocus={(e) => e.preventDefault()}>
          <SearchableSelectSearch value={search} onChange={setSearch} placeholder={searchPlaceholder ?? t('status.search')} headerSlot={headerSlot} />
          <SearchableSelectList options={filtered} hasOptions={options.length > 0} value={value} listRef={listRef} onKeyDown={onKeyDown} onPick={pick} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export default SearchableSelect;
